/**
 * Managing one camera.
 *
 * Four actions, and the interesting one is credentials. A camera password gets
 * changed — by an installer, by the customer, by whoever set up the camera before
 * we arrived — and that must not mean losing the camera. Rotation replaces the
 * sealed value in place; the camera keeps its identity and its recording history.
 *
 * Removal is the destructive one and says so, because it takes the segment index
 * and every event with it.
 */

import { useState } from 'react';

import { sealCameraCredential } from '@jeprotect/shared/camera-credential';

import { ApiError, deleteCamera, updateCamera, updateCameraCredentials } from './api.ts';
import type { CameraRowModel } from './deviceDetail.ts';
import { useDeviceKey } from './useDeviceKey.ts';

type Panel = 'none' | 'rename' | 'credentials';

export function CameraRow({
  deviceId,
  camera,
  onChanged,
}: {
  deviceId: string;
  camera: CameraRowModel;
  onChanged: () => void;
}): React.JSX.Element {
  const [panel, setPanel] = useState<Panel>('none');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [name, setName] = useState(camera.displayName);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const key = useDeviceKey(deviceId, panel === 'credentials');

  function failed(err: unknown, fallback: string): void {
    if (err instanceof ApiError && err.code === 'credentials.key_mismatch') {
      setError(
        'This device’s encryption key changed while this form was open. Reload the page and enter the credentials again.',
      );
    } else if (err instanceof ApiError && err.code === 'device.no_public_key') {
      setError('This device has not finished claiming itself, so credentials cannot be saved yet.');
    } else {
      setError(err instanceof ApiError ? err.message : fallback);
    }
  }

  async function rename(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // Blank clears the name, and the display falls back to the address rather
      // than rendering an empty row.
      await updateCamera(camera.id, { name: name.trim() === '' ? null : name.trim() });
      setPanel('none');
      onChanged();
    } catch (err) {
      failed(err, 'Could not rename the camera.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Turning recording off, and on again.
   *
   * Not a delete. A camera taken offline for a season, or one that keeps flapping
   * and filling the disk, should stop recording without losing what it already
   * recorded. It still occupies licence capacity, which is deliberate — capacity
   * cannot be gamed by toggling cameras off.
   */
  async function toggleRecording(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updateCamera(camera.id, { recordingEnabled: !camera.recordingEnabled });
      onChanged();
    } catch (err) {
      failed(err, 'Could not change recording for this camera.');
    } finally {
      setBusy(false);
    }
  }

  async function saveCredentials(): Promise<void> {
    if (username.trim() === '' || password === '') {
      setError('Both a username and a password are required.');
      return;
    }
    if (key.status !== 'ready') {
      setError('The device encryption key is not available, so credentials cannot be saved.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      /*
       * Sealed in this function, to this device. The plaintext never reaches a
       * request body, and the fingerprint travels with the ciphertext so the
       * server can refuse anything sealed to a key the device no longer holds.
       */
      await updateCameraCredentials(camera.id, {
        sealed: await sealCameraCredential(key.publicKey, {
          username: username.trim(),
          password,
        }),
        keyFingerprint: key.fingerprint,
      });

      setUsername('');
      setPassword('');
      setPanel('none');
      setNotice('Credentials updated. The device picks them up on its next connection.');
      onChanged();
    } catch (err) {
      failed(err, 'Could not save the credentials.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    const confirmed = window.confirm(
      `Remove ${camera.displayName}?\n\n` +
        'This deletes the camera and its recording index, including every event ' +
        'recorded from it. To stop recording without losing the history, disable ' +
        'it instead.',
    );
    if (!confirmed) return;

    setBusy(true);
    setError(null);
    try {
      await deleteCamera(camera.id);
      onChanged();
    } catch (err) {
      failed(err, 'Could not remove the camera.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={`camera-row camera-row--${camera.state}`}>
      <div className="camera-row__main">
        <div className="camera-row__ident">
          <span className="camera-row__name">{camera.displayName}</span>
          {camera.address ? <span className="camera-row__addr">{camera.address}</span> : null}
        </div>

        <div className="camera-row__meta">
          <span className={`pill pill--${camera.state}`}>{camera.stateLabel}</span>
          {/* Stated explicitly, because a disabled camera showing "Offline"
              would otherwise read as a fault to go and investigate. */}
          {camera.recordingEnabled ? null : <span className="pill pill--muted">Recording off</span>}
          <span className="camera-row__creds">{camera.credentialsLabel}</span>
        </div>
      </div>

      <div className="camera-row__actions">
        <button
          type="button"
          className="btn btn--sm"
          disabled={busy}
          onClick={() => {
            setName(camera.displayName);
            setPanel(panel === 'rename' ? 'none' : 'rename');
          }}
        >
          Rename
        </button>

        <button
          type="button"
          className="btn btn--sm"
          disabled={busy || !camera.canSetCredentials}
          title={
            camera.canSetCredentials
              ? undefined
              : 'This device has not published an encryption key yet.'
          }
          onClick={() => setPanel(panel === 'credentials' ? 'none' : 'credentials')}
        >
          {camera.hasCredentials ? 'Change password' : 'Set credentials'}
        </button>

        <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void toggleRecording()}>
          {camera.recordingEnabled ? 'Disable' : 'Enable'}
        </button>

        <button type="button" className="btn btn--sm btn--danger" disabled={busy} onClick={() => void remove()}>
          Remove
        </button>
      </div>

      {panel === 'rename' ? (
        <form
          className="camera-row__panel form"
          onSubmit={(e) => {
            e.preventDefault();
            void rename();
          }}
        >
          <div className="field">
            <label htmlFor={`rename-${camera.id}`}>Camera name</label>
            <input
              id={`rename-${camera.id}`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Front door"
            />
          </div>
          <p className="form__hint">Leave blank to show the camera’s address instead.</p>
          <div className="form__actions">
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setPanel('none')}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {panel === 'credentials' ? (
        <form
          className="camera-row__panel form"
          onSubmit={(e) => {
            e.preventDefault();
            void saveCredentials();
          }}
        >
          <div className="field">
            <label htmlFor={`cu-${camera.id}`}>Camera username</label>
            <input
              id={`cu-${camera.id}`}
              value={username}
              autoComplete="off"
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              disabled={key.status !== 'ready'}
            />
          </div>
          <div className="field">
            <label htmlFor={`cp-${camera.id}`}>Camera password</label>
            <input
              id={`cp-${camera.id}`}
              type="password"
              value={password}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
              disabled={key.status !== 'ready'}
            />
          </div>

          {/* Both facts an operator needs before typing: we cannot read it back,
              and this replaces rather than re-adds, so history survives. */}
          {key.status === 'ready' ? (
            <p className="form__hint">
              Encrypted in your browser to this device. JE Protect cannot read it and cannot show it
              again — only replace it. The camera keeps its recording history.
            </p>
          ) : null}
          {key.status === 'loading' ? <p className="form__hint">Loading the device key…</p> : null}
          {key.status === 'absent' ? (
            <p className="notice notice--warn">
              This device has not claimed itself yet, so credentials cannot be saved.
            </p>
          ) : null}
          {key.status === 'error' ? <p className="notice notice--error">{key.message}</p> : null}

          <div className="form__actions">
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={busy || key.status !== 'ready'}
            >
              {busy ? 'Saving…' : camera.hasCredentials ? 'Replace credentials' : 'Save credentials'}
            </button>
            <button type="button" className="btn btn--sm" onClick={() => setPanel('none')}>
              Cancel
            </button>
          </div>
        </form>
      ) : null}

      {error ? (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="notice notice--ok" role="status">
          {notice}
        </p>
      ) : null}
    </li>
  );
}
